/**
 * Cross-tab session synchronisation.
 *
 * Carries only event signals + an activity timestamp — never token values. Each
 * tab reads the actual access/refresh token from the shared localStorage cache
 * managed by auth0-spa-js, so nothing sensitive ever travels over the channel.
 *
 * Primary transport is a single BroadcastChannel. When BroadcastChannel is
 * unavailable (e.g. Safari < 15.4) it falls back to relaying each message
 * through a localStorage key: the `storage` event fires only in OTHER tabs
 * (never the writer), matching BroadcastChannel's no-self-echo semantics, so
 * cross-tab activity / token-updated / signout still propagate.
 */

export type SessionMessage = { type: 'activity'; ts: number } | { type: 'token-updated' } | { type: 'signout' };

const CHANNEL_NAME = 'dh-session';
const FALLBACK_STORAGE_KEY = 'dh-session-message';

const isSupported = typeof BroadcastChannel !== 'undefined';
const channel: BroadcastChannel | null = isSupported ? new BroadcastChannel(CHANNEL_NAME) : null;

const handlers = new Set<(message: SessionMessage) => void>();

const dispatch = (message: SessionMessage): void => {
  handlers.forEach((handler) => handler(message));
};

// Neither transport echoes a message back to the tab that sent it, so fanning
// received messages out to local handlers cannot self-loop.
channel?.addEventListener('message', (event: MessageEvent<SessionMessage>) => {
  dispatch(event.data);
});

if (!isSupported && typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== FALLBACK_STORAGE_KEY || !event.newValue) {
      return;
    }
    try {
      dispatch(JSON.parse(event.newValue) as SessionMessage);
    } catch {
      // Ignore a malformed payload rather than break the listener.
    }
  });
}

export const postSession = (message: SessionMessage): void => {
  if (channel) {
    channel.postMessage(message);
    return;
  }
  // Fallback: write then immediately clear so even repeated identical messages
  // fire a `storage` event in sibling tabs (setItem to an unchanged value is a no-op).
  try {
    localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(message));
    localStorage.removeItem(FALLBACK_STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode / quota) — degrade to per-tab signout.
  }
};

/** Registers a receiver and returns an unsubscribe function. */
export const onSession = (handler: (message: SessionMessage) => void): (() => void) => {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
};
